/**
 * cardTint — Severity-driven header tint derivation for the
 * Risks and Issues cards on the Performance Summary tab.
 *
 * DEV-853: pure functions that take the (key) risks/issues currently
 * displayed in the Summary card and return a tint level for the
 * header band:
 *
 *   'red'    — section deserves urgent attention
 *   'yellow' — section deserves attention
 *   null     — neutral (no tint applied)
 *
 * The cards on the Summary tab show only `isKeyRisk` / `isKeyIssue`
 * items; this derivation operates on that filtered set so the header
 * tint matches what the user can see in the body. Closed risks and
 * resolved/closed issues are excluded from the input.
 *
 * Severity vocabulary mapping (DEV-853 spec → actual API):
 *
 *   Risks
 *     "High" severity      = score (likelihood × impact) >= 10
 *     "Critical" severity  = score >= 15
 *     status "Realized"    = status === 'materialized'
 *     "future-looking"     = identified | mitigating | accepted
 *
 *   Issues
 *     "Critical" severity  = severity === 'blocking'
 *     "Warning" severity   = severity === 'warning'
 *     "Open"               = status 'open' or 'in_progress'
 *
 * Conservative default for Critical+Identified risks: yellow (not red)
 * until status becomes 'materialized'. Discussed in DEV-853.
 */

import type { ProjectRiskResponse } from '../../api/projectRisks';
import type { ProjectIssueResponse } from '../../api/projectIssues';

export type CardTint = 'red' | 'yellow' | null;

/** Score thresholds derived from the riskScoreColor.ts hue breakpoints
 *  (1, 3, 6, 10, 15, 25). These two values mark the orange and red
 *  bands respectively, so reusing them keeps the tint logic aligned
 *  with the visual gradient already on screen for individual scores. */
export const RISK_SCORE_HIGH = 10;
export const RISK_SCORE_CRITICAL = 15;

/**
 * Derive tint for the Key Risks card.
 *
 * Activation rules (DEV-853):
 *   • Red    — any 'materialized' risk with score >= 15 (Critical+Realized)
 *   • Yellow — any score >= 10 (High+) OR any 'materialized' risk
 *   • null   — otherwise (low/medium severity in any non-materialized
 *              status, or empty list)
 */
export function deriveRisksTint(keyRisks: ProjectRiskResponse[]): CardTint {
  // 'closed' risks are settled; ignore.
  const open = keyRisks.filter((r) => r.status !== 'closed');
  if (open.length === 0) return null;

  const score = (r: ProjectRiskResponse): number =>
    (r.likelihood ?? 1) * (r.impact ?? 1);

  // Red: Realized AND Critical-severity.
  if (
    open.some((r) => r.status === 'materialized' && score(r) >= RISK_SCORE_CRITICAL)
  ) {
    return 'red';
  }

  // Yellow: any High-severity OR any Realized.
  if (open.some((r) => score(r) >= RISK_SCORE_HIGH || r.status === 'materialized')) {
    return 'yellow';
  }

  return null;
}

/**
 * Derive tint for the Key Issues card.
 *
 * Active = status 'open' OR 'in_progress'. Resolved / closed issues
 * are excluded — they're settled work and shouldn't drive attention.
 *
 * Activation rules (DEV-853):
 *   • Red    — any active 'blocking' issue (Critical-severity)
 *   • Yellow — any active 'warning' issue
 *   • null   — only 'informational' issues active, or empty list
 *
 * Note: the ticket also lists "any issue blocking a milestone" as a
 * Red trigger. The current Issue data model has no explicit milestone-
 * blocker flag (only a generic linkedTaskIds array), so that rule is
 * not implemented here. Add when the data shape supports it.
 */
export function deriveIssuesTint(keyIssues: ProjectIssueResponse[]): CardTint {
  const active = keyIssues.filter(
    (i) => i.status === 'open' || i.status === 'in_progress',
  );
  if (active.length === 0) return null;

  if (active.some((i) => i.severity === 'blocking')) return 'red';
  if (active.some((i) => i.severity === 'warning')) return 'yellow';
  return null;
}
